import { Link } from "@inertiajs/react";
import Suspense from "components/shared/Suspense";
import ExpandingText from "components/ui/ExpandingText";
import Avatar from "hardcover-ui/components/Avatar";
import Heading from "hardcover-ui/components/Heading";
import useCurrentUserId from "hooks/user/useCurrentUserId";
import { lazy } from "react";
import { PageSerializersPromptsLayout, UserSerializersUserByline } from "types/serializers";

const EditPromptLink = lazy(() => import("./EditPromptLink"));
const PinPromptButton = lazy(() => import("./PinPromptButton"));

export interface Props {
  prompt: PageSerializersPromptsLayout;
  currentUserId?: number;
}

function PromptAuthor({ user }: { user: UserSerializersUserByline }) {
  return (
    <div className="flex gap-2 items-center mb-4">
      <span className="font-semibold">By</span>
      <Avatar
        image={user.image}
        size="sm"
        alt={`@${user.username}`}
        variant="bordered"
      />
      <Link
        href={`/@${user.username}`}
        className="rounded-sm text-indigo-600 dark:text-indigo-300 font-semibold dark:hover:text-white hover:text-gray-600 hover:underline"
      >
        <span>{user.name}</span>
        {user.flair && user.flair.length > 0 && (
          <span className="ml-2 bg-yellow-300 text-gray-900 rounded-sm px-1 py-0.5 text-xs">
            {user.flair}
          </span>
        )}
      </Link>
    </div>
  );
}

function PromptCounts({
  prompt,
}: {
  prompt: PageSerializersPromptsLayout;
}) {
  const currentUserId = useCurrentUserId();
  return (
    <div className="col-span-12 lg:col-span-3">
      <div className="flex flex-row lg:flex-col gap-2 flex-wrap mt-4 items-end">
        <div className="text-sm hidden lg:flex">
          <span className="bg-indigo-600 dark:bg-indigo-600 text-yellow-50 py-1 px-2 rounded-l-lg">
            Books
          </span>
          <span className="bg-gray-400 dark:bg-gray-700 text-yellow-50 py-1 px-2 rounded-r-lg">
            {prompt.booksCount && <>{prompt.booksCount.toLocaleString()}</>}{" "}
          </span>
        </div>
        <div className="hidden lg:flex text-sm">
          <span className="bg-yellow-500 dark:bg-yellow-400 text-gray-800 py-1 px-2 rounded-l-lg">
            Answers
          </span>
          <span className="bg-gray-400 dark:bg-gray-700 text-yellow-50 py-1 px-2 rounded-r-lg">
            {prompt.answersCount && <>{prompt.answersCount.toLocaleString()}</>}{" "}
          </span>
        </div>
        <div className="hidden lg:flex text-sm">
          <span className="bg-green-500 dark:bg-green-600 py-1 px-2 rounded-l-lg">
            Readers
          </span>
          <span className="bg-gray-400 dark:bg-gray-700 text-yellow-50 py-1 px-2 rounded-r-lg">
            {prompt.usersCount && <>{prompt.usersCount.toLocaleString()}</>}{" "}
          </span>
        </div>
        <Suspense>
          <>
            {currentUserId === prompt.userId ? (
              <EditPromptLink prompt={prompt} />
            ) : (
              false
            )}
            {currentUserId > 0 ? (
              <PinPromptButton promptId={prompt.id} />
            ) : (
              false
            )}
          </>
        </Suspense>
      </div>
    </div>
  );
}

export default function PromptDescriptionInfo({
  prompt,
}: Props) {
  return (
    <div className="grid grid-cols-12 md:gap-8 pb-8 lg:pb-0">
      <div className="col-span-12 lg:col-span-9">
        <Heading variant="primary">{prompt.question}</Heading>
        <PromptAuthor user={prompt.user} />
        <ExpandingText text={prompt.description} lines={5} />
      </div>

      <PromptCounts prompt={prompt} />
    </div>
  );
}
